import { z } from "zod";

export const ADD_USER_BADGE_PATTERN = /^[a-zA-Z0-9]{1,10}$/;

/**
 * Paired refines keep exactly ONE message per failing field, in the legacy
 * order — a chained .min().regex() would emit both issues for an empty
 * string and change csvParser's joined error strings.
 *
 * Note: this badge rule (alphanumeric) is deliberately different from the
 * Profile form's digits-only rule below.
 */
export const newUserSchema = z.object({
  firstName: z.string().trim().min(1, "First Name is required"),
  lastName: z.string().trim().min(1, "Last Name is required"),
  position: z.string().trim().min(1, "Position is required"),
  badgeNumber: z
    .string()
    .trim()
    .refine((v) => v.length > 0, "Badge Number is required")
    .refine(
      (v) => v === "" || ADD_USER_BADGE_PATTERN.test(v),
      "Badge Number must be 1 to 10 alphanumeric characters",
    ),
  department: z.string().trim().min(1, "Department is required"),
  email: z
    .string()
    .trim()
    .refine((v) => v.length > 0, "Email is required")
    .refine((v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), "Invalid email format"),
  isAdmin: z.boolean().optional(),
});

export type NewUser = z.infer<typeof newUserSchema>;

/** csvParser-compatible adapter: same messages, same field order. */
export const validateUser = (user: NewUser): string[] => {
  const result = newUserSchema.safeParse(user);
  return result.success ? [] : result.error.issues.map((issue) => issue.message);
};

export const PROFILE_BADGE_PATTERN = /^\d{1,10}$/;

export const profileMetadataSchema = z.object({
  badgeNumber: z
    .string()
    .trim()
    .min(1, "Badge number is required.")
    .regex(PROFILE_BADGE_PATTERN, "Badge number must be 1-10 digits."),
  position: z.string().min(1, "Job title is required."),
  department: z.string().trim().min(1, "Department is required."),
});

export type ProfileMetadataValues = z.infer<typeof profileMetadataSchema>;
